import { Icon } from "../ui/Icon.jsx";
import { useTypewriter } from "../hooks/useTypewriter.js";
import { useCountUp } from "../hooks/useCountUp.js";

export const INDUSTRIES = [
  {
    key: "ecommerce",
    label: "E-commerce",
    target: 18472,
    phrases: [
      "high-value runners in the US who bought in the last 30 days",
      "lapsed customers we haven't seen in 60+ days",
      "repeat buyers likely to churn this quarter",
      "shoppers who abandoned a cart over $100",
    ],
    rows: ["Profile · country in US · LTV ≥ $200", "Purchases · running shoes · within 30d", "Events · opened email · within 14d"],
    problemBodies: [
      "They look a lot like your best customers today, but nothing in your stack can tell you who they are. So you rent broad audiences and hope the right ones are in there.",
      "Sales in one system. Conversations in another. Marketing in a third. Years of history in spreadsheets and inboxes. Each one knows a fraction of the same customer, and nothing joins them up, so every decision gets made on a partial view.",
      "After the order confirmation, most customers get the same newsletter as everyone else. The ones drifting away hear nothing until they're gone, and a second purchase is left to chance.",
    ],
    whatExamples: [
      "shoppers who look like your best hundred customers",
      "what \"a good customer\" means in your store, not in a generic template",
      "email, SMS, paid social, on-site",
    ],
    stack: [
      { icon: "Store", name: "Your store", kind: "Source · Destination" },
      { icon: "Mail", name: "Email & SMS", kind: "Destination" },
      { icon: "Megaphone", name: "Paid social", kind: "Destination" },
      { icon: "Search", name: "Search", kind: "Destination" },
      { icon: "Slack", name: "Slack", kind: "Surface" },
      { icon: "Database", name: "Warehouse", kind: "Source" },
    ],
  },
  {
    key: "service",
    label: "Service contracting",
    target: 412,
    phrases: [
      "homeowners due for a seasonal HVAC tune-up",
      "leads who requested a quote but didn't book",
      "customers with a warranty expiring this month",
      "past jobs eligible for a maintenance upsell",
    ],
    rows: ["Service area · within 25 miles", "Last job · 10-14 months ago", "Contract · warranty active"],
    problemBodies: [
      "The work you're best at and the buyers who need it are both on public record. Nobody connects the two, so you end up bidding on whatever arrives instead of what you'd win.",
      "Jobs in your field software. Contacts in a CRM nobody maintains. Delivery history in permit registers and agency records. Credentials in a filing cabinet. Nothing joins it up, so buyers see a fraction of the company.",
      "A job wraps up, the invoice goes out, and the relationship goes quiet until something breaks. Service agreements lapse without a conversation, and repeat work goes to whoever called last.",
    ],
    whatExamples: [
      "the work you've delivered that your own website never mentions",
      "what a renewal looks like in your service book",
      "capability pages, bid collateral, email, local search",
    ],
    stack: [
      { icon: "Database", name: "Your CRM", kind: "Source · Destination" },
      { icon: "Gear", name: "Field software", kind: "Source" },
      { icon: "CSV", name: "Public records", kind: "Source" },
      { icon: "Mail", name: "Email & SMS", kind: "Destination" },
      { icon: "Search", name: "Search & local", kind: "Destination" },
      { icon: "Store", name: "Your website", kind: "Destination" },
    ],
  },
  {
    key: "enterprise",
    label: "Enterprise software",
    target: 700,
    phrases: [
      "accounts trending toward churn this renewal cycle",
      "free-tier teams hitting usage limits weekly",
      "champions who haven't logged in for 21 days",
      "accounts ready for an upsell conversation",
    ],
    rows: ["Plan · free tier · seats ≥ 5", "Usage · 80%+ of quota · 7d", "Health score · declining 2 weeks"],
    problemBodies: [
      "The companies most likely to buy already run the platforms you integrate with and have the problem you solve. But they search for the problem in their words, not your category, so they find a competitor or a workaround before they find you.",
      "Your website is instrumented in detail. Then a prospect reads the docs, starts a trial or downloads the free tier, and goes quiet. Which accounts are evaluating you right now, and where they stalled, is a question nobody can answer until they book a call.",
      "Your best proof lives in your customers' conference talks and engineering blogs, not on your pages. When a buyer asks \"who else runs this at our scale?\" they get a logo strip, and after the deal closes the relationship goes quiet until renewal.",
    ],
    whatExamples: [
      "the accounts evaluating you on the docs site before anyone books a call",
      "how buyers and AI assistants describe the problem you solve, and where your own pages disagree with each other",
      "reference program, trust page, analyst briefings, partner profiles, in-product opt-ins",
    ],
    stack: [
      { icon: "Database", name: "Your CRM", kind: "Source · Destination" },
      { icon: "Bolt", name: "Product usage", kind: "Source" },
      { icon: "Plug", name: "Partner ecosystems", kind: "Source" },
      { icon: "Mail", name: "Email & SMS", kind: "Destination" },
      { icon: "Search", name: "Search & AI answers", kind: "Destination" },
      { icon: "Slack", name: "Slack", kind: "Surface" },
    ],
  },
];

export function HeroMock({ activeIndex, onChange, stealthLevel = 6 }) {
  const industries = INDUSTRIES;
  const active = industries[activeIndex];

  const typed = useTypewriter(active.phrases);
  const count = useCountUp(active.target, 1200);

  return (
    <div className="hero-mock" style={{ "--stealth": `${stealthLevel}px` }}>
      <div className="hm-head">
        <div className="hm-dots">
          <span className="hm-dot"></span>
          <span className="hm-dot"></span>
          <span className="hm-dot"></span>
        </div>
        <span className="hm-url">ngineroom.ai / find</span>
      </div>
      <div className="hm-tabs">
        {industries.map((ind, i) => (
          <button
            key={ind.key}
            className={`hm-tab${i === activeIndex ? " hm-tab-active" : ""}`}
            onClick={() => onChange(i)}
          >
            {ind.label}
          </button>
        ))}
      </div>
      <div className="hm-body">
        <div className="hm-prompt">
          <div className="hm-avatar">YOU</div>
          <div className="hm-bubble">
            Find me {typed}
            <span className="hm-caret"></span>
          </div>
        </div>
        <div className="hm-stat-row">
          <div className="hm-stat">{count.toLocaleString()}</div>
          <div className="hm-stat-label">customers match</div>
        </div>
        <div className="hm-rows">
          {active.rows.map((r, i) => (
            <div key={i} className="hm-row">
              <span className="ico"><Icon.Check /></span>
              {r}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
